import { getUserInfo } from "redux/selectors";
import { useSelector } from "react-redux";
import { useUpdate } from "hooks/useUpdate";
import { notification } from "antd";
import { InfoCircleFilled } from "@ant-design/icons";
import queryKeys from "common/queryKey";
import moment from "moment";
import styled from "styled-components";
import Button from "atoms/Button";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import { useQueryState } from "hooks/useQueryState";
import { useQueryClient } from "react-query";
import { useState } from "react";
import DeclineTimesheetModal from "./DeclineTimesheetModal";
import { CALL_API } from "common/API";

const ApproveActionButton = ({
  selectedTimesheetKeys,
  setSelectedTimesheetKeys,
}) => {
  const userInfo = useSelector((state) => getUserInfo(state));
  const queryClient = useQueryClient();

  const { updateTimesheet, getTimesheet } = queryKeys;

  const { week, status } = useParams();

  const [loading, setLoading] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);

  const { data: timesheetDataRes } = useQueryState(
    `${queryKeys.getTimesheet.key}-week:${week}`
  );

  const timesheetData = timesheetDataRes?.response?.response || {};

  const getActualSummary = async (matterId, userId) => {
    try {
      const { data } = await CALL_API(
        `/insyts/timesheet/actualsummary?matterId=${matterId}&userId=${userId}`,
        "get"
      );

      return data;
    } catch (error) {
      console.error("Failed to fetch actualToDate:", error);
      return null;
    }
  };

  const approveTimesheet = () => {
    setLoading(true);
    const updatedTimeSheet = [];
    const newTimeSheet = [...timesheetData.timeSheet];

    for (let i = 0; i < selectedTimesheetKeys?.length; i++) {
      const idx = selectedTimesheetKeys[i];

      if (newTimeSheet[idx]) {
        newTimeSheet[idx] = {
          ...newTimeSheet[idx],
          status: "approved",
          approval: {
            by: userInfo.employer_id,
            date: moment().format("DD-MM-YYYY"),
          },
        };
        updatedTimeSheet.push(newTimeSheet[idx]);
      }
    }

    approveUpdate(
      {
        ...timesheetData,
        updatedTimeSheet,
        timeSheet: newTimeSheet,
      },
      {
        onSuccess: async () => {
          queryClient.invalidateQueries(`${getTimesheet.key}-week:${week}`);
          const updatedTimeSheet = await Promise.all(
            (timesheetData?.timeSheet || []).map(async (item) => {
              const actualSummary = await getActualSummary(
                item?.engagement,
                timesheetData?.employeeId
              );

              if (item.personInCharge === userInfo?.userid) {
                return {
                  ...actualSummary,
                  actualTimesheet: actualSummary?.actualTimesheet?.map(
                    (summaryItem) => ({
                      ...summaryItem,
                      status:
                        summaryItem?.week === timesheetData?.week
                          ? "approved"
                          : summaryItem?.status,
                    })
                  ),
                };
              }

              return item;
            })
          );
          for (const entry of updatedTimeSheet) {
            if (entry?.actualTimesheet) {
              const { code } = await CALL_API(
                `insyts/timesheet/actualsummary`,
                "patch",
                entry
              );
            }
          }
          setLoading(false);
          notification.success({
            message: "Timesheets Approved Successfully",
          });
          setSelectedTimesheetKeys([]);
        },
      }
    );
  };

  const { mutate: approveUpdate } = useUpdate(
    updateTimesheet.key,
    updateTimesheet.url
  );

  return (
    <>
      {status === "pending" && (
        <FooterBar>
          <div className="footer-summary">
            {/* <InfoCircleFilled className="summary-icon" />
            <span>Weekly Summary</span> */}
          </div>

          <div className="footer-actions">
            <Button
              className="cancel-btn"
              onClick={() => setSelectedTimesheetKeys([])}
            >
              Cancel
            </Button>

            <Button
              className="decline-btn-outline"
              disabled={!selectedTimesheetKeys?.length > 0}
              onClick={() => {
                setShowDeclineModal(true);
              }}
              title="Decline Timesheet"
            >
              Decline
            </Button>

            <Button
              className="approve-btn"
              disabled={!selectedTimesheetKeys?.length > 0}
              onClick={approveTimesheet}
              loading={loading}
            >
              Approve
            </Button>
          </div>

          <DeclineTimesheetModal
            open={showDeclineModal}
            setOpen={setShowDeclineModal}
            selectedTimesheetKeys={selectedTimesheetKeys}
            setSelectedTimesheetKeys={setSelectedTimesheetKeys}
          />
        </FooterBar>
      )}
    </>
  );
};

export default ApproveActionButton;

const FooterBar = styled.div`
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #ffffff;
  border-top: 1px solid #e2e2e8;
  padding: 12px 24px;
  margin: 0 -24px;
  z-index: 10;

  .footer-summary {
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: "DM Sans", sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #1f243b;

    .summary-icon {
      font-size: 16px;
      color: #0066ff;
    }
  }

  .footer-actions {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .cancel-btn,
  .cancel-btn:hover,
  .cancel-btn:focus,
  .cancel-btn:active {
    background: #ffffff !important;
    border: none !important;
    color: #1f243b !important;
    box-shadow: none !important;
  }

  .decline-btn-outline,
  .decline-btn-outline:hover,
  .decline-btn-outline:focus,
  .decline-btn-outline:active {
    background: #ffffff !important;
    border: 1px solid #d9d9d9 !important;
    color: #1f243b !important;
  }

  .approve-btn,
  .approve-btn:hover,
  .approve-btn:focus,
  .approve-btn:active {
    background: #00685f !important;
    border-color: #00685f !important;
    color: #ffffff !important;
  }

  .decline-btn-outline:disabled,
  .approve-btn:disabled {
    opacity: 0.5;
  }
`;